import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  TableHead,
  CircularProgress,
  Alert,
  Modal,
  Typography,
  useMediaQuery,
  GlobalStyles,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from "react-router-dom";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

// Import all previews
import ARMHSProgressNotePreview from "./Preview/ARMHSProgressNotePreview";
import ARMHSCrisisPlanPreview from "./Preview/ARMHSCrisisPlanPreview";
import ARMHSDischargeSummaryPreview from "./Preview/ARMHSDischargeSummaryPreview";
import ARMHSITPPreview from "./Preview/ARMHSITPPreview";
import ARMHSFunctionalAssessmentPreview from "./Preview/ARMHSFunctionalAssessmentPreview";
import ARMHSFunctionalAssessmentNewPreview from "./Preview/ARMHSFunctionalAssessmentNewPreview";
import DiagnosticAssessmentPreview from "./Preview/DiagnosticAssessmentPreview";
import AdultDiagnosticAssessmentPreview from "./Preview/AdultDiagnosticAssessmentPreview";
import AODASupervisionNotePreview from "./Preview/AODASupervisionNotePreview";
import GAD7Preview from "./Preview/GAD7Preview";
import CollateralNotePreview from "./Preview/CollateralNotePreview";
import NurseVisitPreview from "./Preview/NurseVisitPreview";
import PostpartumVisitPreview from "./Preview/PostpartumVisitPreview";
import PrenatalVisitPreview from "./Preview/PrenatalVisitPreview";
import IntakeFormPreview from "./Preview/IntakeFormPreview";
import BirthOverviewPreview from "./Preview/BirthOverviewPreview";
import PHQ9Preview from "./Preview/PHQ9Preview";
import BaselineQuestionairePreview from "./Preview/BaselinePreview";
import ClientSessionVerificationPreview from "./Preview/ClientSessionpreview";
import MHISClientFormPreview from "./Preview/MHISClientpreview";
import OBGYNPreview from "./Preview/OBGYNPreview";
import apiClient from "../../Utiles/apiClient";

// Pagination Controls
function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const theme = useTheme();
  const totalPages = Math.ceil(count / rowsPerPage);
  return (
    <Box sx={{ display: "flex", gap: 1, p: "0 16px" }}>
      <IconButton
        onClick={(e) => onPageChange(e, page - 1)}
        disabled={page === 0}
      >
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page + 1 >= totalPages}
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
    </Box>
  );
}

const FormsList = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewType, setPreviewType] = useState(null);


  const location = useLocation();
  const navigate = useNavigate();


  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClosePreview = useCallback(() => {
    setOpenPreview(false);
    setPreviewData(null);
    setPreviewType(null);
  }, []);

  // Helper function to check if signature exists
  const hasSignature = (value) => {
    if (!value) return false;
    if (value === null || value === undefined) return false;
    if (value === "null" || value === "undefined") return false;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "" || trimmed === "EMPTY") return false;
      return true;
    }
    return false;
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch paginated forms
  const fetchForms = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(
        `/PatientPortal/GetPatientForms?PageNumber=${page + 1}&pageSize=${rowsPerPage}`
      );

      console.log("Response status:", res.status);

      if (res.status === 404) {
        setForms([]);
        setTotalCount(0);
        setError("No forms found for the patient.");
        return;
      }

      if (!res.ok) throw new Error(`Failed to fetch forms: ${res.status}`);

      const data = await res.json();
    
      let formsArray = [];
      let total = 0;

      if (Array.isArray(data)) {
        formsArray = data;
        total = data[0]?.TotalRows || data.length;
      } else if (data?.data && Array.isArray(data.data)) {
        formsArray = data.data;
        total = data.totalRows || data.TotalRows || data.data.length;
      } else if (data?.items && Array.isArray(data.items)) {
        formsArray = data.items;
        total = data.totalCount || data.items.length;
      } else if (data?.result && Array.isArray(data.result)) {
        formsArray = data.result;
        total = data.totalRows || data.result.length;
      } else if (data?.Data && Array.isArray(data.Data)) {
        formsArray = data.Data;
        total = data.TotalRows || data.Data.length;
      }

      if (formsArray.length === 0) {
        setForms([]);
        setTotalCount(0);
        setError("No forms found for the patient.");
        return;
      }

      setForms(formsArray);
      setTotalCount(total);
    } catch (err) {
      console.error("Fetch error:", err);
      // Don't show session expired errors (already handled by redirect)
      if (!err.message.includes("Session expired")) {
        setError(err.message);
      }
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);


  // Handle view click
  const handleView = useCallback((id, title) => {
    const normalized = (title || "").toLowerCase().trim();
    const formMap = {
      "armhs progress note": "ARMHSProgressNote",
      "armhs crisis plan": "ARMHSCrisisPlan",
      "armhs discharge summary": "ARMHSDischargeSummary",
      "armhs itp": "ARMHSITP",
      "armhs functional assessment new": "ARMHSFunctionalAssessmentNew",
      "armhs functional assessment": "ARMHSFunctionalAssessment",
      "adult diagnostic assessment": "AdultDiagnosticAssessment",
      "diagnostic assessment": "DiagnosticAssessment",
      "collateral note": "CollateralNote",
      "aoda supervision": "AODASupervisionNote",
      "aoda collaboration": "AODASupervisionNote",
      "gad7": "GAD7",
      "nurse visit": "NurseVisit",
      "postpartum visit": "PostpartumVisit",
      "prenatal visit": "PrenatalVisit",
      "intake form": "IntakeForm",
      "birth overview": "BirthOverview",
      "phq9": "PHQ9",
      "baseline questionnaire": "BaselineQuestionaire",
      "baseline questionaire": "BaselineQuestionaire",
      "client session verification": "ClientSessionVerification",
      "mhis client form": "MHISClientForm",
      "mhis client level data": "MHISClientForm",
      "obgyn": "OBGYN",
    };

    const formType =
      Object.entries(formMap).find(([k]) => normalized.includes(k))?.[1] ||
      "Generic";

    setPreviewData({ patientFormId: id, formTitle: title });
    setPreviewType(formType);
    setOpenPreview(true);
  }, []);



  useEffect(() => {
    fetchForms();
  }, [fetchForms]);


  
  // ✅ AUTO-OPEN preview if user came from Dashboard "Today's Forms"
useEffect(() => {
  if (location.state?.openFormId && location.state?.openFormTitle) {
    handleView(location.state.openFormId, location.state.openFormTitle);

    // ✅ Clear the state so refresh doesn't re-open the modal
    navigate(location.pathname, { replace: true, state: {} });
  }
}, [location.state, handleView, navigate, location.pathname]);


  // --- STYLES ---

  const stickyHeaderSx = {
    fontWeight: "bold",
    fontSize: "1rem",
    zIndex: 10,
    backgroundColor: "#fff",
    color: "#000",
    ".dark-theme &": {
      backgroundColor: "#0e1726",
      color: "#fff !important",
      borderBottom: "1px solid #1f2937",
    },
  };

  const bodyCellStyle = {
    ".dark-theme &": {
      color: "#fff !important",
      borderBottom: "1px solid #1f2937",
    },
  };

  const breadcrumbSx = {
    ".dark-theme &": {
      "& h5": { color: "#fff !important" },
      "& .breadcrumb": {
        "& li": { color: "#fff !important" },
        "& li a": { color: "#fff !important" },
        "& i": { color: "#fff !important" }
      }
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", md: "80%", lg: "70%" },
    borderRadius: 2,
    boxShadow: 24,
    overflow: "hidden",

    // Default Light Mode
    bgcolor: "#fff",
    color: "#000",

    // Dark Mode Override
    ".dark-theme &": {
      bgcolor: "#0e1726",
      color: "#fff",
      border: "1px solid #3b3f5c"
    },
  };

  // --- PREVIEW WRAPPER (DARK MODE FIXES) ---
  const previewWrapperSx = {
    maxHeight: "90vh",
    overflow: "auto",
    p: 2,

    // ═══════════════════════════════════════════════════════
    // 🌙 DEEP DARK MODE OVERRIDES
    // ═══════════════════════════════════════════════════════
    ".dark-theme &": {
      // ─────────────────────────────────────────────────────
      // 1. GLOBAL TEXT & CONTAINERS
      // ─────────────────────────────────────────────────────
      color: "#fff !important",
      "& .MuiPaper-root, & .MuiCard-root, & .MuiBox-root, & .MuiAccordion-root, & .MuiDialog-paper": {
        backgroundColor: "#0e1726 !important",
        color: "#fff !important",
        backgroundImage: "none !important",
        boxShadow: "none !important",
        border: "none",
      },

      // ─────────────────────────────────────────────────────
      // 2. TEXT VISIBILITY (ALL TEXT ELEMENTS)
      // ─────────────────────────────────────────────────────
      "& h1, & h2, & h3, & h4, & h5, & h6, & p, & span, & div, & label, & strong, & b, & em, & i, & u, & small, & td, & th, & li, & a": {
        color: "#fff !important",
      },

      // ─────────────────────────────────────────────────────
      // 3. INLINE STYLE OVERRIDES (for hardcoded colors)
      // ─────────────────────────────────────────────────────
      "& [style*='background-color: white'], & [style*='background-color: #fff'], & [style*='background: white'], & [style*='background: #fff'], & [style*='background:#fff'], & [style*='background-color:#fff']": {
        backgroundColor: "#0e1726 !important",
      },
      "& [style*='color: black'], & [style*='color:#000'], & [style*='color: #000']": {
        color: "#fff !important",
      },
      "& [style*='background: #f5f5f5'], & [style*='background-color: #f5f5f5']": {
        backgroundColor: "#1b2e4b !important",
      },
      "& [style*='border: 1px solid black'], & [style*='border: 2px solid #000'], & [style*='border:1px solid black']": {
        borderColor: "rgba(255, 255, 255, 0.3) !important",
      },

      // ─────────────────────────────────────────────────────
      // 4. INPUTS & TEXTAREAS (Bright & Sharp)
      // ─────────────────────────────────────────────────────
      "& input, & textarea, & select": {
        backgroundColor: "transparent !important",
        border: "none !important",
        color: "#FFFFFF !important",
        fontWeight: 500,
      },
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent !important",
        borderRadius: "4px",
        color: "#FFFFFF !important",
      },
      "& .MuiOutlinedInput-root input, & .MuiOutlinedInput-root textarea": {
        border: "none !important",
        backgroundColor: "transparent !important",
        padding: "8.5px 14px !important",
        color: "#FFFFFF !important",
        fontWeight: 500,
        WebkitTextFillColor: "#FFFFFF !important", // ✅ Fixes readonly/disabled fade
        opacity: "1 !important",
      },
      // ✅ Fix for readonly / disabled inputs that appear grayed out
      "& .MuiInputBase-input.Mui-disabled, & .MuiInputBase-input[readonly]": {
        WebkitTextFillColor: "#FFFFFF !important",
        color: "#FFFFFF !important",
        opacity: "1 !important",
      },

      // ─────────────────────────────────────────────────────
      // 5. LABELS (Bright, sharp, readable — NO blur)
      // ─────────────────────────────────────────────────────
      "& .MuiInputLabel-root": {
        color: "#FFFFFF !important",
        backgroundColor: "transparent !important",
        fontWeight: 500,
        opacity: "1 !important",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "#8B87F7 !important",
        fontWeight: 600,
      },
      "& .MuiInputLabel-shrink": {
        backgroundColor: "transparent !important",
        padding: "0 !important",
        color: "#FFFFFF !important",
        fontWeight: 600,
      },
      "& .MuiInputLabel-root.Mui-disabled": {
        color: "#FFFFFF !important",
        opacity: "0.9 !important",
      },

      // ─────────────────────────────────────────────────────
      // 5b. OUTLINED INPUT BORDERS & NOTCH (Fixes label overlap)
      // ─────────────────────────────────────────────────────
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 255, 255, 0.3) !important",
      },
      "& .MuiOutlinedInput-notchedOutline legend": {
        backgroundColor: "transparent !important",
      },
      "& .MuiOutlinedInput-notchedOutline legend span": {
        backgroundColor: "transparent !important",
        paddingLeft: "5px !important",
        paddingRight: "5px !important",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 255, 255, 0.5) !important",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#8B87F7 !important",
        borderWidth: "1px !important",
      },

      // ─────────────────────────────────────────────────────
      // 6. BUTTONS
      // ─────────────────────────────────────────────────────
      "& .MuiButton-root, & button, & .MuiButton-contained": {
        backgroundColor: "#1b2e4b !important",
        color: "#fff !important",
        border: "1px solid rgba(255, 255, 255, 0.2) !important",
      },
      "& .MuiButton-root:hover, & button:hover": {
        backgroundColor: "#3b3f5c !important",
      },
      // Keep primary/action buttons visible
      "& .MuiButton-containedPrimary": {
        backgroundColor: "#4361ee !important",
        color: "#fff !important",
      },
      "& .MuiButton-containedPrimary:hover": {
        backgroundColor: "#3651d4 !important",
      },
      // Icon buttons stay transparent
      "& .MuiIconButton-root": {
        backgroundColor: "transparent !important",
        border: "none !important",
      },
      "& .MuiIconButton-root:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.08) !important",
      },

      // ─────────────────────────────────────────────────────
      // 7. LINKS
      // ─────────────────────────────────────────────────────
      "& .MuiLink-root, & a": {
        color: "#8B87F7 !important",
      },
      "& .MuiLink-root:hover, & a:hover": {
        color: "#a5a2f9 !important",
      },

      // ─────────────────────────────────────────────────────
      // 8. TABLES (including HTML tables with inline styles)
      // ─────────────────────────────────────────────────────
      "& table, & thead, & tbody, & tfoot, & tr, & th, & td": {
        backgroundColor: "transparent !important",
        color: "#fff !important",
        borderColor: "rgba(255, 255, 255, 0.25) !important",
      },
      "& table[style*='border']": {
        borderColor: "rgba(255, 255, 255, 0.25) !important",
      },
      "& th[style*='color'], & td[style*='color']": {
        color: "#fff !important",
      },

      // ─────────────────────────────────────────────────────
      // 9. CHECKBOXES & RADIOS
      // ─────────────────────────────────────────────────────
      "& .MuiCheckbox-root, & .MuiRadio-root": {
        color: "rgba(255, 255, 255, 0.7) !important",
      },
      "& .MuiCheckbox-root.Mui-checked, & .MuiRadio-root.Mui-checked": {
        color: "#4361ee !important",
      },
      "& .MuiCheckbox-root.Mui-disabled, & .MuiRadio-root.Mui-disabled": {
        color: "rgba(255, 255, 255, 0.4) !important",
      },
      "& .MuiFormControlLabel-label": {
        color: "#fff !important",
      },
      "& .MuiFormControlLabel-label.Mui-disabled": {
        color: "rgba(255, 255, 255, 0.6) !important",
      },
      "& input[type='checkbox'], & input[type='radio']": {
        accentColor: "#4361ee !important",
        backgroundColor: "transparent !important",
      },

      // ─────────────────────────────────────────────────────
      // 10. DIVIDERS
      // ─────────────────────────────────────────────────────
      "& .MuiDivider-root, & hr": {
        borderColor: "rgba(255, 255, 255, 0.15) !important",
        backgroundColor: "rgba(255, 255, 255, 0.15) !important",
      },

      // ─────────────────────────────────────────────────────
      // 11. TOOLTIPS
      // ─────────────────────────────────────────────────────
      "& .MuiTooltip-tooltip": {
        backgroundColor: "#1b2e4b !important",
        color: "#fff !important",
        border: "1px solid rgba(255,255,255,0.15) !important",
      },

      // ─────────────────────────────────────────────────────
      // 12. SIGNATURES (Force white paper look)
      // ─────────────────────────────────────────────────────
      "& canvas, & .signature-pad, & img[src*='sign'], & img[alt*='Sign'], & img[alt*='ign'], & .react-signature-canvas": {
        backgroundColor: "#ffffff !important",
        filter: "none !important",
        border: "1px solid #ccc !important",
        borderRadius: "4px",
        padding: "5px !important",
        maxWidth: "100%",
        display: "block",
      },
      // Signature wrapper divs (with inline styles)
      "& div[style*='border: 2px solid #000'], & div[style*='background: #f5f5f5'], & div[style*='background:#f5f5f5']": {
        backgroundColor: "#ffffff !important",
        border: "1px solid #ccc !important",
      },

      // ─────────────────────────────────────────────────────
      // 13. STICKY HEADERS (used in previews)
      // ─────────────────────────────────────────────────────
      "& [style*='position: sticky'], & [class*='sticky']": {
        backgroundColor: "#0e1726 !important",
      },

      // ─────────────────────────────────────────────────────
      // 14. CHIPS (status badges)
      // ─────────────────────────────────────────────────────
      "& .MuiChip-root": {
        color: "#fff !important",
      },

      // ─────────────────────────────────────────────────────
      // 15. LIST ITEMS
      // ─────────────────────────────────────────────────────
      "& .MuiListItem-root, & .MuiListItemText-primary, & .MuiListItemText-secondary": {
        color: "#fff !important",
      },

      // ─────────────────────────────────────────────────────
      // 16. SELECTS & MENUS
      // ─────────────────────────────────────────────────────
      "& .MuiSelect-select, & .MuiSelect-icon": {
        color: "#fff !important",
      },

      // ─────────────────────────────────────────────────────
      // 17. TYPOGRAPHY VARIANTS
      // ─────────────────────────────────────────────────────
      "& .MuiTypography-root": {
        color: "#fff !important",
      },
      "& .MuiTypography-colorTextSecondary": {
        color: "rgba(255, 255, 255, 0.7) !important",
      },
    },
  };

  const closeButtonStyle = {
    position: "absolute",
    right: 20,
    top: 10,
    zIndex: (theme) => theme.zIndex.modal + 100,
    color: "red",

    // Light Mode
    background: "#f0f0f0",
    "&:hover": { background: "#e0e0e0" },

    // Dark Mode
    ".dark-theme &": {
      background: "#3b3f5c",
      "&:hover": { background: "#1b2e4b" },
      color: "#ff6b6b"
    },
  };

  const paginationSx = {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    mt: 2,
    ".dark-theme &": {
      borderTop: '1px solid #1f2937',
      color: "#fff !important",
      ".MuiTablePagination-selectLabel": { color: "#fff !important" },
      ".MuiTablePagination-displayedRows": { color: "#fff !important" },
      ".MuiTablePagination-select": { color: "#fff !important" },
      ".MuiTablePagination-selectIcon": { color: "#fff !important" },
      ".MuiIconButton-root": {
        color: "#fff !important",
        "&.Mui-disabled": { color: "rgba(255,255,255,0.3) !important" }
      }
    }
  };

  if (loading)
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );



  return (
    <>
      <GlobalStyles
        styles={{
          "*::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: "#bdbdbd",
            borderRadius: "3px",
          },
          // 1. DARK MODE DIALOGS (MUI)
          ".dark-theme .MuiDialog-container": {
            "& .MuiDialogTitle-root": {
              backgroundColor: "#0e1726",
              color: "#fff",
            },
            "& .MuiDialogContent-root": {
              backgroundColor: "#0e1726",
            },
            "& .MuiDialogContent-root [style*='border: 1px solid #000']": {
              borderColor: 'rgba(255, 255, 255, 0.3) !important',
            },
            "& .MuiDialogActions-root": {
              backgroundColor: "#0e1726",
            },
            "& .MuiButton-contained": {
              color: "#fff",
            },
          },
          // 2. DARK MODE NOTIFICATIONS
          ".dark-theme .MuiAlert-root, .dark-theme .MuiSnackbarContent-root": {
            backgroundColor: "#1b2e4b !important",
            color: "#fff !important",
            border: "1px solid #3b3f5c !important",
          },
          ".dark-theme .MuiAlert-icon": {
            color: "#fff !important",
          },

          // 3. DARK MODE SWEETALERT2 POPUPS
          ".dark-theme .swal2-popup": {
            backgroundColor: "#1b2e4b !important",
            color: "#fff !important",
            border: "1px solid #3b3f5c !important",
          },
          ".dark-theme .swal2-title": {
            color: "#fff !important",
          },
          ".dark-theme .swal2-content, .dark-theme .swal2-html-container": {
            color: "#fff !important",
          },
          ".dark-theme .swal2-confirm": {
            backgroundColor: "#4361ee !important",
            color: "#fff !important",
            boxShadow: "none !important",
          },
          ".dark-theme .swal2-cancel": {
            backgroundColor: "#e7515a !important",
            color: "#fff !important",
          },
          ".dark-theme div[style*='background-color: rgb(255, 255, 255)'], .dark-theme div[style*='background-color: #fff']": {
            backgroundColor: "#0e1726 !important",
            color: "#fff !important",
          },
          ".dark-theme .MuiDialog-paper": {
            backgroundColor: "#0e1726 !important",
            color: "#fff !important",
          },
          ".dark-theme .MuiDialogTitle-root, .dark-theme .MuiDialogContent-root, .dark-theme .MuiDialogActions-root": {
            backgroundColor: "#0e1726 !important",
            color: "#fff !important",
          },
        }}
      />

      <Box className="breadcrumb-card" sx={breadcrumbSx}>
        <h5>Forms</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/Dashboard">
              <i className="material-symbols-outlined">home</i> Dashboard
            </Link>
          </li>
          <li>Forms</li>
        </ul>
      </Box>

      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            maxHeight: 'calc(100vh - 210px)',
            "&::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#bdbdbd",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            // ✅ Allow horizontal scroll on very small screens so table stays readable
            overflowX: "auto",
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{ tableLayout: "fixed", width: "100%", minWidth: 500 }}
          >
            <colgroup>
              <col style={{ width: "15%" }} />
              <col style={{ width: "65%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>

            <TableHead>
              <TableRow>
                <TableCell sx={{ ...stickyHeaderSx, px: 3, textAlign: "left" }}>
                  ID
                </TableCell>
                <TableCell sx={{ ...stickyHeaderSx, px: 3, textAlign: "center" }}>
                  Form Title
                </TableCell>
                <TableCell sx={{ ...stickyHeaderSx, px: 3, textAlign: "right" }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {forms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={bodyCellStyle}>
                    <Typography>No forms available.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                forms.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ ...bodyCellStyle, px: 3, textAlign: "left" }}>
                      {f.id}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellStyle,
                        px: 3,
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {f.customFormTitle || f.Formtitle || "Untitled"}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellStyle, px: 3, textAlign: "right" }}>
                      <IconButton
                        color="primary"
                        onClick={() =>
                          handleView(f.id, f.Formtitle || f.customFormTitle)
                        }
                      >
                        <VisibilityIcon
                          fontSize="small"
                          sx={{ ".dark-theme &": { color: "#3651d4" } }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50]}
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          sx={paginationSx}
        />
      </Card>

      <Modal open={openPreview} onClose={handleClosePreview}>
        <Box sx={modalStyle}>
          <IconButton
            onClick={handleClosePreview}
            sx={closeButtonStyle}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={previewWrapperSx}>
            {previewType === "CollateralNote" && (
              <CollateralNotePreview data={previewData} />
            )}
            {previewType === "ARMHSProgressNote" && (
              <ARMHSProgressNotePreview data={previewData} />
            )}
            {previewType === "ARMHSCrisisPlan" && (
              <ARMHSCrisisPlanPreview data={previewData} />
            )}
            {previewType === "ARMHSDischargeSummary" && (
              <ARMHSDischargeSummaryPreview data={previewData} />
            )}
            {previewType === "ARMHSITP" && (
              <ARMHSITPPreview data={previewData} />
            )}
            {previewType === "ARMHSFunctionalAssessment" && (
              <ARMHSFunctionalAssessmentPreview data={previewData} />
            )}
            {previewType === "ARMHSFunctionalAssessmentNew" && (
              <ARMHSFunctionalAssessmentNewPreview data={previewData} />
            )}
            {previewType === "DiagnosticAssessment" && (
              <DiagnosticAssessmentPreview data={previewData} />
            )}
            {previewType === "AdultDiagnosticAssessment" && (
              <AdultDiagnosticAssessmentPreview data={previewData} />
            )}
            {previewType === "AODASupervisionNote" && (
              <AODASupervisionNotePreview data={previewData} />
            )}
            {previewType === "GAD7" && <GAD7Preview data={previewData} />}
            {previewType === "NurseVisit" && (
              <NurseVisitPreview data={previewData} />
            )}
            {previewType === "PostpartumVisit" && (
              <PostpartumVisitPreview data={previewData} />
            )}
            {previewType === "PrenatalVisit" && (
              <PrenatalVisitPreview data={previewData} />
            )}
            {previewType === "IntakeForm" && (
              <IntakeFormPreview data={previewData} />
            )}
            {previewType === "BirthOverview" && (
              <BirthOverviewPreview data={previewData} />
            )}
            {previewType === "PHQ9" && <PHQ9Preview data={previewData} />}
            {previewType === "BaselineQuestionaire" && (
              <BaselineQuestionairePreview data={previewData} />
            )}
            {previewType === "ClientSessionVerification" && (
              <ClientSessionVerificationPreview data={previewData} />
            )}
            {previewType === "MHISClientForm" && (
              <MHISClientFormPreview data={previewData} />
            )}
            {previewType === "OBGYN" && (
              <OBGYNPreview data={previewData} />
            )}
            {previewType === "Generic" && (
              <Box sx={{ textAlign: "center", p: 3 }}>
                <Typography variant="h6" color="textSecondary">
                  Preview not available
                </Typography>
                <Typography variant="body2">
                  {previewData?.formTitle || "Unknown form"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FormsList;